import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Divider,
  CircularProgress,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import { verifyOtp } from "../api";

const VerifyOtpModal = ({ open, onClose, email }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [otp, setOtp] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isOtpValid = useMemo(() => {
    const v = (otp || "").trim();
    // keep UX nice: allow 4–8 digits (common OTP lengths)
    return /^\d{4,8}$/.test(v);
  }, [otp]);

  const handleChange = (e) => {
    setErrorMessage("");
    // numeric-only UX without changing your API/logic
    const onlyDigits = e.target.value.replace(/\D/g, "");
    setOtp(onlyDigits);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);

      const response = await verifyOtp({ email, otp }); // API call
      const sellerId = response.data?.sellerId;

      if (sellerId) {
        localStorage.setItem("sellerId", sellerId.trim()); // ✅ Store trimmed sellerId
        console.log("✅ sellerId stored in localStorage:", sellerId.trim());
      } else {
        console.error("❌ sellerId missing in response");
      }

      Swal.fire({
        icon: "success",
        title: "Verification Successful",
        text: "Your account has been verified. Please log in to continue.",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/payment", { state: { sellerId } });
      });

      onClose();
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "OTP verification failed");
      setOtp("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.16)",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          p: 0,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(25,118,210,0.25), rgba(156,39,176,0.12))"
              : "linear-gradient(135deg, rgba(25,118,210,0.12), rgba(156,39,176,0.08))",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.25,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.25,
              display: "grid",
              placeItems: "center",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(25,118,210,0.28)"
                  : "rgba(25,118,210,0.12)",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(144,202,249,0.25)"
                  : "rgba(25,118,210,0.18)"
              }`,
            }}
          >
            <LockRoundedIcon color="primary" />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
              Verify OTP
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
              Enter the one-time password sent to your email
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            disabled={submitting}
            sx={{
              borderRadius: 2,
              color: "text.secondary",
              "&:hover": { color: "text.primary" },
            }}
            aria-label="Close"
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <Divider />
      </DialogTitle>

      {/* Body */}
      <DialogContent sx={{ px: 3, pt: 2.5, pb: 0 }}>
        <Box
          sx={{
            display: "flex",
            gap: 1.25,
            alignItems: "flex-start",
            p: 2,
            borderRadius: 2,
            backgroundColor:
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"
            }`,
          }}
        >
          <MarkEmailReadRoundedIcon color="primary" sx={{ mt: 0.25 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              OTP sent to
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                wordBreak: "break-word",
              }}
            >
              {email}
            </Typography>
          </Box>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2.5 }}>
          <TextField
            label="One-time password"
            variant="outlined"
            fullWidth
            value={otp}
            onChange={handleChange}
            required
            autoFocus
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: 8,
              style: { letterSpacing: "0.35em", fontWeight: 700 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            helperText={
              errorMessage ? (
                <Typography component="span" sx={{ color: "error.main", fontWeight: 600 }}>
                  {errorMessage}
                </Typography>
              ) : (
                "Enter 4–8 digit OTP"
              )
            }
            FormHelperTextProps={{ sx: { mx: 0, mt: 1 } }}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              "& .MuiInputLabel-root": { fontWeight: 600 },
            }}
            error={!!errorMessage}
          />

          {/* Micro UX hint */}
          <Typography variant="caption" sx={{ display: "block", mt: 1.25, color: "text.secondary" }}>
            Tip: Check your spam/junk folder if you don’t see the email.
          </Typography>
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 2.5, flexDirection: "column", gap: 1.25 }}>
        <Button
          onClick={handleSubmit}
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={submitting || !otp.trim() || !isOtpValid}
          sx={{
            textTransform: "none",
            fontWeight: 800,
            py: 1.4,
            borderRadius: 2,
            boxShadow: "0 10px 24px rgba(25,118,210,0.22)",
          }}
          startIcon={submitting ? <CircularProgress size={18} /> : null}
        >
          {submitting ? "Verifying..." : "Verify OTP"}
        </Button>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Didn’t receive OTP?
          </Typography>

          {/* Same UI, no API/logic changes */}
          <Button
            color="secondary"
            size="small"
            disabled={submitting}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              px: 1.25,
            }}
          >
            Resend
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default VerifyOtpModal;